// src/componants/manager/AppealsManager.js
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  respondToAppeal,
  getAppealTransferTargets,
  transferAppeal,
} from "../../api/appeals";
import {
  isHrFamilyRole,
  canHrApproveRequests,
  isOwnerRole,
  isPrimaryHrRole,
} from "../../utils/hrStaffHelpers";
import AppealSubjectDetails from "../common/AppealSubjectDetails";
import EmployeePunchDaysModal from "../common/EmployeePunchDaysModal";
import {
  getAppealPunchMonth,
  getAppealSubject,
} from "../../utils/appealSubject";
import { getAppealLevelLabel, isAppealSkipLevelReviewer } from "../../utils/appealCycle";
import {
  isGmAppealActionRequired,
  isGmAppealViewOnly,
} from "../../utils/appealHrDeadline";
import AppealDeadlineClock from "../common/AppealDeadlineClock";
import {
  isCrossBranchAppealForUser,
  isEmployeeUnderManager,
  isOrphanedSameBranchAppeal,
} from "../../utils/reportingTree";
import { toIsoDate } from "../../utils/fillMonthPunchDays";
import "./AppealsManager.css";

const appealTypes = {
  penalty: "تظلم على جزاء",
  request: "تظلم على رفض طلب",
  advance: "تظلم على سلفة",
  leave: "تظلم على إجازة",
};

const levelOrder = ["manager", "regionalManager", "departmentManager", "hr"];

function AppealsManager({ userRole }) {
  const { user } = useAuth();
  const {
    appeals,
    refreshData,
    applyAppealDecision,
    employeesByBranch,
    employeePenalties,
    allPenalties,
  } = useData();
  const penalties = allPenalties?.length ? allPenalties : employeePenalties || [];

  const [filter, setFilter] = useState(
    userRole === "employee" || userRole === "gm" ? "all" : "actionRequired",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [responseModal, setResponseModal] = useState({
    open: false,
    appealId: null,
    action: "",
    response: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [punchModal, setPunchModal] = useState(null);
  const [transferModal, setTransferModal] = useState({
    open: false,
    appealId: null,
    targetKey: "",
    notes: "",
  });
  const [transferTargets, setTransferTargets] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);

  // Employee profile id — managerId on reports points here (not auth user.id)
  const currentEmployeeId = user?.employeeId || null;
  const isRegionalManager = user?.backendRoles?.includes("regional_manager");

  const resolveActorLevel = () => {
    if (canHrApproveRequests(userRole)) return "hr";
    if (userRole === "department-manager") return "departmentManager";
    if (isRegionalManager) return "regionalManager";
    if (userRole === "manager") return "manager";
    return null;
  };

  const visibleAppeals = (() => {
    if (userRole === "employee") {
      return appeals.filter(
        (a) => String(a.employeeId) === String(currentEmployeeId),
      );
    }

    if (isRegionalManager || userRole === "department-manager") {
      return appeals.filter((a) => {
        const employee = Object.values(employeesByBranch)
          .flat()
          .find((e) => String(e.id) === String(a.employeeId));
        return (
          String(employee?.departmentBackendId) ===
            String(user?.departmentBackendId) ||
          String(employee?.departmentId) === String(user?.departmentId) ||
          a.levels?.manager?.status ||
          a.levels?.regionalManager?.status
        );
      });
    }

    if (userRole === "manager") {
      const allEmployees = Object.values(employeesByBranch).flat();
      return appeals.filter((a) => {
        if (isCrossBranchAppealForUser(a, user, allEmployees)) return false;
        if (String(a.managerId) === String(currentEmployeeId)) return true;
        if (isAppealSkipLevelReviewer(a, currentEmployeeId)) return true;
        if (isOrphanedSameBranchAppeal(a, user, allEmployees)) return true;
        return isEmployeeUnderManager(
          allEmployees,
          currentEmployeeId,
          a.employeeId,
        );
      });
    }

    if (isHrFamilyRole(userRole) || userRole === "gm" || isOwnerRole(userRole)) {
      return appeals;
    }

    return [];
  })();

  function canRespond(appeal) {
    if (userRole === "employee") return false;
    if (
      currentEmployeeId &&
      String(appeal.employeeId) === String(currentEmployeeId)
    ) {
      return false;
    }

    if (userRole === "gm") {
      return isGmAppealActionRequired(appeal);
    }

    if (
      (isPrimaryHrRole(userRole) || userRole === "admin") &&
      !appeal.finalStatus &&
      String(appeal.status || "").includes("pending")
    ) {
      return true;
    }

    const userLevel = resolveActorLevel();
    if (!userLevel) return false;

    const allEmployees = Object.values(employeesByBranch || {}).flat();
    if (isCrossBranchAppealForUser(appeal, user, allEmployees)) return false;
    const isDirectManager =
      currentEmployeeId &&
      String(appeal.managerId) === String(currentEmployeeId);
    const coversOrphan = isOrphanedSameBranchAppeal(appeal, user, allEmployees);
    if (
      (isDirectManager || coversOrphan) &&
      appeal.status === "manager_pending" &&
      !appeal.finalStatus
    ) {
      return true;
    }

    if (
      isAppealSkipLevelReviewer(appeal, currentEmployeeId) &&
      appeal.status === "regionalManager_pending" &&
      !appeal.finalStatus
    ) {
      return true;
    }

    if (appeal.currentLevel !== userLevel) return false;
    if (appeal.status !== `${userLevel}_pending`) return false;

    return true;
  }

  function canTransfer(appeal) {
    if (!canHrApproveRequests(userRole)) return false;
    if (appeal.finalStatus) return false;
    if (
      currentEmployeeId &&
      String(appeal.employeeId) === String(currentEmployeeId)
    ) {
      return false;
    }
    return String(appeal.status || "").includes("pending");
  }

  function transferTargetKey(row) {
    return `${row.role}::${row.id}`;
  }

  const filteredAppeals = visibleAppeals
    .filter((a) => {
      if (filter === "all") return true;
      if (filter === "actionRequired") return canRespond(a);
      if (filter === "viewOnly") return isGmAppealViewOnly(a);
      if (filter === "pending") return a.status.includes("pending");
      if (filter === "resolved") return a.finalStatus !== null;
      return true;
    })
    .filter((a) => {
      if (!searchTerm) return true;
      const subject = getAppealSubject(a, penalties);
      return (
        a.employeeName?.includes(searchTerm) ||
        a.employeeCode?.includes(searchTerm) ||
        a.reason?.includes(searchTerm) ||
        subject?.whatHappened?.includes(searchTerm) ||
        subject?.typeLabel?.includes(searchTerm) ||
        subject?.dateLabel?.includes(searchTerm)
      );
    })
    .sort((a, b) => {
      const aAction = canRespond(a) ? 1 : 0;
      const bAction = canRespond(b) ? 1 : 0;
      if (aAction !== bAction) return bAction - aAction;
      return new Date(b.submittedAt) - new Date(a.submittedAt);
    });

  const pendingCount = visibleAppeals.filter((a) =>
    a.status.includes("pending"),
  ).length;
  const actionRequiredCount = visibleAppeals.filter(canRespond).length;
  const viewOnlyCount = visibleAppeals.filter(isGmAppealViewOnly).length;
  const canSeeActionFilter = userRole !== "employee";

  const canViewPunchDays = userRole !== "employee";

  function openPunchDays(appeal) {
    if (!canViewPunchDays || !appeal?.employeeId) return;
    const { month, year } = getAppealPunchMonth(appeal, penalties);
    const subject = getAppealSubject(appeal, penalties);
    setPunchModal({
      employeeId: appeal.employeeId,
      employeeName: appeal.employeeName,
      employeeCode: appeal.employeeCode,
      month,
      year,
      highlightDate: toIsoDate(subject?.date),
    });
  }

  function openResponseModal(appealId, action) {
    setResponseModal({ open: true, appealId, action, response: "" });
    setError("");
  }

  function closeResponseModal() {
    setResponseModal({ open: false, appealId: null, action: "", response: "" });
    setError("");
  }

  async function openTransferModal(appealId) {
    setTransferModal({
      open: true,
      appealId,
      targetKey: "",
      notes: "",
    });
    setError("");
    setTransferTargets([]);
    setTransferLoading(true);
    try {
      const rows = await getAppealTransferTargets(appealId);
      const list = Array.isArray(rows) ? rows : [];
      setTransferTargets(list);
      if (list.length === 1) {
        setTransferModal((prev) => ({
          ...prev,
          targetKey: transferTargetKey(list[0]),
        }));
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر تحميل أعضاء السايكل";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setTransferLoading(false);
    }
  }

  function closeTransferModal() {
    setTransferModal({
      open: false,
      appealId: null,
      targetKey: "",
      notes: "",
    });
    setTransferTargets([]);
    setError("");
  }

  async function handleConfirmTransfer() {
    const { appealId, targetKey, notes } = transferModal;
    const selected = transferTargets.find(
      (row) => transferTargetKey(row) === targetKey,
    );
    if (!selected) {
      setError("اختر مستلماً من سايكل التظلم");
      return;
    }
    if (!notes.trim() || notes.trim().length < 3) {
      setError("يجب إدخال سبب التحويل");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const updated = await transferAppeal(appealId, {
        targetRole: selected.role,
        targetId: selected.id,
        notes: notes.trim(),
      });
      applyAppealDecision(updated);
      closeTransferModal();
      refreshData({ silent: true, scope: "appeals" });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر تحويل التظلم";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmResponse() {
    const { appealId, action, response } = responseModal;
    if (!response.trim()) {
      setError("يجب إدخال رد على التظلم");
      return;
    }

    const appeal = appeals.find((a) => a.id === appealId);
    if (!appeal) {
      setError("التظلم غير موجود");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const updated = await respondToAppeal(appealId, {
        action,
        response: response.trim(),
      });
      applyAppealDecision(updated);
      closeResponseModal();
      refreshData({ silent: true, scope: "appeals" });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر حفظ الرد على التظلم";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  function getStatusDisplay(appeal) {
    if (appeal.finalStatus === "approved") {
      return (
        <span className="appeal-status-badge status-approved">
          ✅ تم القبول نهائيًا
        </span>
      );
    }
    if (appeal.finalStatus === "rejected") {
      return (
        <span className="appeal-status-badge status-rejected">
          ❌ مرفوض نهائيًا
        </span>
      );
    }

    const approvedLevels = levelOrder.filter(
      (level) => appeal.levels?.[level]?.status === "approved",
    );
    const lastApproved =
      approvedLevels.length > 0
        ? getAppealLevelLabel(
            approvedLevels[approvedLevels.length - 1],
            appeal,
          )
        : null;

    let waitingLabel = getAppealLevelLabel("manager", appeal);
    if (appeal.gmActionRequired) waitingLabel = "المدير العام";
    else if (appeal.status === "hr_pending") waitingLabel = getAppealLevelLabel("hr", appeal);
    else if (appeal.status === "departmentManager_pending")
      waitingLabel = getAppealLevelLabel("departmentManager", appeal);
    else if (appeal.status === "regionalManager_pending")
      waitingLabel = getAppealLevelLabel("regionalManager", appeal);

    if (lastApproved) {
      return (
        <span className="appeal-status-badge status-dm">
          ✅ مقبول من {lastApproved} — بانتظار {waitingLabel}
        </span>
      );
    }

    if (appeal.gmActionRequired) {
      return (
        <span className="appeal-status-badge status-gm">
          ⏳ بانتظار المدير العام
        </span>
      );
    }
    if (appeal.status === "hr_pending") {
      return (
        <span className="appeal-status-badge status-hr">
          ⏳ عند {getAppealLevelLabel("hr", appeal)}
        </span>
      );
    }
    if (appeal.status === "departmentManager_pending") {
      return (
        <span className="appeal-status-badge status-dm">
          ⏳ عند {getAppealLevelLabel("departmentManager", appeal)}
        </span>
      );
    }
    if (appeal.status === "regionalManager_pending") {
      return (
        <span className="appeal-status-badge status-dm">
          ⏳ عند {getAppealLevelLabel("regionalManager", appeal)}
        </span>
      );
    }
    return (
      <span className="appeal-status-badge status-pending">
        ⏳ عند {getAppealLevelLabel("manager", appeal)}
      </span>
    );
  }

  function getTypeLabel(type) {
    return appealTypes[type] || type;
  }

  function getLevelHistory(appeal) {
    const history = [];
    levelOrder.forEach((level) => {
      const levelData = appeal.levels?.[level];
      if (!levelData?.status || levelData.status === "pending") return;
      history.push({
        level: getAppealLevelLabel(level, appeal),
        status: levelData.status === "approved" ? "قبول" : "رفض",
        response: levelData.response,
        respondedBy: levelData.respondedBy,
        respondedAt: levelData.respondedAt,
        kind: "decision",
      });
    });
    (appeal.transfers || []).forEach((entry) => {
      history.push({
        level: `${getAppealLevelLabel(entry.from, appeal) || entry.from} → ${
          getAppealLevelLabel(entry.to, appeal) || entry.to
        }`,
        status: "تحويل",
        response: entry.notes,
        respondedBy: entry.respondedBy,
        respondedAt: entry.respondedAt,
        kind: "transfer",
        targetName: entry.targetName,
      });
    });
    return history;
  }

  return (
    <div className="appeals-manager" dir="rtl">
      <div className="appeals-header">
        <div>
          <h2 className="appeals-title">
            {userRole === "employee" ? "تظلماتي" : "إدارة التظلمات"}
          </h2>
          <p className="appeals-subtitle">
            {userRole === "gm"
              ? `${visibleAppeals.length} تظلم | ${actionRequiredCount} يحتاج إجراء الآن | ${viewOnlyCount} اطلاع فقط`
              : `${visibleAppeals.length} تظلم | ${pendingCount} قيد المراجعة${
                  canSeeActionFilter
                    ? ` | ${actionRequiredCount} تحتاج إجراء منك`
                    : ""
                }`}
          </p>
        </div>
      </div>

      <div className="appeals-filters">
        {canSeeActionFilter && (
          <button
            className={`filter-btn filter-btn-action ${filter === "actionRequired" ? "active" : ""}`}
            onClick={() => setFilter("actionRequired")}
          >
            {userRole === "gm" ? "يحتاج إجراء الآن" : "مطلوب مني إجراء"}
            <span className="filter-count">{actionRequiredCount}</span>
          </button>
        )}
        {userRole === "gm" && (
          <button
            className={`filter-btn ${filter === "viewOnly" ? "active" : ""}`}
            onClick={() => setFilter("viewOnly")}
          >
            اطلاع فقط
            <span className="filter-count">{viewOnlyCount}</span>
          </button>
        )}
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل
        </button>
        <button
          className={`filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          قيد المراجعة
        </button>
        <button
          className={`filter-btn ${filter === "resolved" ? "active" : ""}`}
          onClick={() => setFilter("resolved")}
        >
          محسوم
        </button>
      </div>

      {userRole !== "employee" && (
        <div className="appeals-search">
          <input
            type="text"
            placeholder="بحث بالموظف أو الكود..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="appeals-search-input"
          />
        </div>
      )}

      <div className="appeals-list">
        {filteredAppeals.length === 0 ? (
          <div className="appeals-empty">
            <p>
              {filter === "actionRequired"
                ? "لا توجد تظلمات تحتاج إجراء منك حالياً"
                : filter === "viewOnly"
                  ? "لا توجد تظلمات داخل المهلة"
                  : "📋 لا توجد تظلمات"}
            </p>
          </div>
        ) : (
          filteredAppeals.map((appeal) => (
            <div
              key={appeal.id}
              className={`appeal-card ${appeal.finalStatus || appeal.status}${
                canRespond(appeal) ? " needs-action" : ""
              }`}
            >
              <div className="appeal-card-header">
                <div className="appeal-employee">
                  <div className="appeal-avatar">
                    {appeal.employeeName?.charAt(0)}
                  </div>
                  <div>
                    {canViewPunchDays && appeal.employeeId ? (
                      <button
                        type="button"
                        className="appeal-employee-name appeal-employee-name-btn"
                        onClick={() => openPunchDays(appeal)}
                        title="عرض أيام البصمة خلال الشهر"
                      >
                        {appeal.employeeName}
                      </button>
                    ) : (
                      <div className="appeal-employee-name">
                        {appeal.employeeName}
                      </div>
                    )}
                    <div className="appeal-employee-code">
                      {appeal.employeeCode}
                    </div>
                    {appeal.jobTitle ? (
                      <div className="appeal-employee-job">
                        {appeal.jobTitle}
                      </div>
                    ) : null}
                    {appeal.workplaceLabel ? (
                      <div className="appeal-employee-workplace">
                        {appeal.workplaceLabel}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="appeal-status-stack">
                  {getStatusDisplay(appeal)}
                  {userRole === "gm" && isGmAppealViewOnly(appeal) && (
                    <span className="appeal-status-badge status-view">
                      اطلاع فقط
                    </span>
                  )}
                  {userRole === "gm" && isGmAppealActionRequired(appeal) && (
                    <span className="appeal-status-badge status-gm">
                      يحتاج إجراءك
                    </span>
                  )}
                </div>
              </div>

              <div className="appeal-details">
                <div className="appeal-detail-row">
                  <span className="appeal-detail-label">نوع التظلم:</span>
                  <span className="appeal-detail-value">
                    {getTypeLabel(appeal.type)}
                  </span>
                </div>
                {!appeal.finalStatus && appeal.hrDecisionDueAt ? (
                  <AppealDeadlineClock dueAt={appeal.hrDecisionDueAt} />
                ) : null}
                <div className="appeal-detail-row">
                  <span className="appeal-detail-label">تاريخ التقديم:</span>
                  <span className="appeal-detail-value">
                    {new Date(appeal.submittedAt).toLocaleDateString("ar-EG")}
                  </span>
                </div>
                <AppealSubjectDetails
                  appeal={appeal}
                  penalties={penalties}
                />
                <div className="appeal-detail-row">
                  <span className="appeal-detail-label">سبب التظلم:</span>
                  <span className="appeal-detail-value appeal-reason">
                    {appeal.reason}
                  </span>
                </div>
                <div className="appeal-detail-row">
                  <span className="appeal-detail-label">المستوى الحالي:</span>
                  <span className="appeal-detail-value">
                    {getAppealLevelLabel(appeal.currentLevel, appeal)}
                  </span>
                </div>
              </div>

              {getLevelHistory(appeal).length > 0 && (
                <div className="appeal-history">
                  <h4 className="appeal-history-title">📋 تاريخ التظلم</h4>
                  {getLevelHistory(appeal).map((h, idx) => (
                    <div
                      key={idx}
                      className={`appeal-history-item ${
                        h.kind === "transfer"
                          ? "transferred"
                          : h.status === "قبول"
                            ? "approved"
                            : "rejected"
                      }`}
                    >
                      <div className="appeal-history-header">
                        <span className="appeal-history-level">{h.level}</span>
                        <span
                          className={`appeal-history-status ${
                            h.kind === "transfer"
                              ? "transferred"
                              : h.status === "قبول"
                                ? "approved"
                                : "rejected"
                          }`}
                        >
                          {h.status}
                        </span>
                      </div>
                      <p className="appeal-history-response">{h.response}</p>
                      <span className="appeal-history-by">
                        بواسطة: {h.respondedBy}
                        {h.targetName ? ` → ${h.targetName}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {(canRespond(appeal) || canTransfer(appeal)) && (
                <div className="appeal-actions">
                  {canRespond(appeal) && (
                    <>
                      <button
                        className="appeal-btn reject"
                        onClick={() => openResponseModal(appeal.id, "rejected")}
                      >
                        ❌ رفض التظلم
                      </button>
                      <button
                        className="appeal-btn approve"
                        onClick={() => openResponseModal(appeal.id, "approved")}
                      >
                        ✅ قبول التظلم
                      </button>
                    </>
                  )}
                  {canTransfer(appeal) && (
                    <button
                      className="appeal-btn transfer"
                      onClick={() => openTransferModal(appeal.id)}
                    >
                      تحويل داخل السايكل
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {punchModal ? (
        <EmployeePunchDaysModal
          employeeId={punchModal.employeeId}
          employeeName={punchModal.employeeName}
          employeeCode={punchModal.employeeCode}
          month={punchModal.month}
          year={punchModal.year}
          highlightDate={punchModal.highlightDate}
          onClose={() => setPunchModal(null)}
        />
      ) : null}

      {transferModal.open && (
        <div className="appeal-modal-overlay" onClick={closeTransferModal}>
          <div className="appeal-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="appeal-modal-title">تحويل التظلم</h3>
            <p className="appeal-modal-subtitle">
              يمكن تحويل التظلم لأي شخص داخل سايكل الموظف
            </p>
            {(() => {
              const appeal = appeals.find(
                (a) => a.id === transferModal.appealId,
              );
              return appeal ? (
                <AppealSubjectDetails appeal={appeal} penalties={penalties} />
              ) : null;
            })()}

            <div className="appeal-modal-field">
              <label className="appeal-modal-label">التحويل إلى</label>
              <select
                className="appeal-modal-select"
                value={transferModal.targetKey}
                onChange={(e) =>
                  setTransferModal((prev) => ({
                    ...prev,
                    targetKey: e.target.value,
                  }))
                }
                disabled={transferLoading || transferTargets.length === 0}
              >
                <option value="">
                  {transferLoading ? "جاري التحميل..." : "اختر مستلماً من السايكل"}
                </option>
                {transferTargets.map((row) => (
                  <option
                    key={transferTargetKey(row)}
                    value={transferTargetKey(row)}
                  >
                    {row.label} — {row.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="appeal-modal-field">
              <label className="appeal-modal-label">سبب التحويل *</label>
              <textarea
                className={`appeal-modal-textarea ${error ? "has-error" : ""}`}
                rows={4}
                placeholder="اكتب سبب التحويل..."
                value={transferModal.notes}
                onChange={(e) => {
                  setTransferModal((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }));
                  if (error) setError("");
                }}
              />
              {error && <p className="appeal-modal-error">{error}</p>}
            </div>

            <div className="appeal-modal-note">
              سيتم إرسال التظلم للمستلم المختار داخل نفس السايكل دون إغلاقه.
            </div>

            <div className="appeal-modal-buttons">
              <button
                className="appeal-modal-btn secondary"
                onClick={closeTransferModal}
              >
                إلغاء
              </button>
              <button
                className="appeal-modal-btn approve"
                onClick={handleConfirmTransfer}
                disabled={submitting || transferLoading || transferTargets.length === 0}
              >
                {submitting ? "جاري التحويل..." : "تأكيد التحويل"}
              </button>
            </div>
          </div>
        </div>
      )}

      {responseModal.open && (
        <div className="appeal-modal-overlay" onClick={closeResponseModal}>
          <div className="appeal-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="appeal-modal-title">
              {responseModal.action === "approved"
                ? "✅ قبول التظلم"
                : "❌ رفض التظلم"}
            </h3>
            <p className="appeal-modal-subtitle">
              {(() => {
                const appeal = appeals.find(
                  (a) => a.id === responseModal.appealId,
                );
                const isFinalStep =
                  appeal?.currentLevel === "hr" ||
                  canHrApproveRequests(userRole);
                if (isFinalStep) {
                  return responseModal.action === "approved"
                    ? "قرار نهائي: سيتم قبول التظلم وإلغاء الجزاء (إن وجد)"
                    : "قرار نهائي: سيتم رفض التظلم نهائيًا";
                }
                return responseModal.action === "approved"
                  ? "سيتم تسجيل قبولك وإرسال التظلم للمستوى الأعلى حتى قرار HR النهائي"
                  : "سيتم تسجيل رفضك وإرسال التظلم للمستوى الأعلى حتى قرار HR النهائي";
              })()}
            </p>
            {(() => {
              const appeal = appeals.find(
                (a) => a.id === responseModal.appealId,
              );
              return appeal ? (
                <AppealSubjectDetails appeal={appeal} penalties={penalties} />
              ) : null;
            })()}

            <div className="appeal-modal-field">
              <label className="appeal-modal-label">رد الإدارة *</label>
              <textarea
                className={`appeal-modal-textarea ${error ? "has-error" : ""}`}
                rows={4}
                placeholder="اكتب ردك على التظلم..."
                value={responseModal.response}
                onChange={(e) => {
                  setResponseModal((prev) => ({
                    ...prev,
                    response: e.target.value,
                  }));
                  if (error) setError("");
                }}
              />
              {error && <p className="appeal-modal-error">{error}</p>}
            </div>

            <div className="appeal-modal-note">
              {(() => {
                const appeal = appeals.find(
                  (a) => a.id === responseModal.appealId,
                );
                const isFinalStep =
                  appeal?.currentLevel === "hr" ||
                  canHrApproveRequests(userRole);
                if (isFinalStep) {
                  return responseModal.action === "approved"
                    ? "⚠️ سيتم إلغاء الجزاء المرتبط وإشعار الموظف بالقبول النهائي"
                    : "⚠️ سيتم إبقاء الجزاء ساريًا وإشعار الموظف بالرفض النهائي";
                }
                return "⚠️ القبول أو الرفض هنا لا يغلق التظلم — يكمل للسلسلة حتى HR";
              })()}
            </div>

            <div className="appeal-modal-buttons">
              <button
                className="appeal-modal-btn secondary"
                onClick={closeResponseModal}
              >
                إلغاء
              </button>
              <button
                className={`appeal-modal-btn ${responseModal.action === "approved" ? "approve" : "reject"}`}
                onClick={handleConfirmResponse}
                disabled={submitting}
              >
                {submitting
                  ? "جاري الحفظ..."
                  : responseModal.action === "approved"
                    ? "تأكيد القبول"
                    : "تأكيد الرفض"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppealsManager;
